import React from "react";
import Modal from "./Modal";

interface ImportConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  localCategoriesCount: number;
  localPromptsCount: number;
  importCategoriesCount: number;
  importPromptsCount: number;
  isRemoteSync?: boolean;
  // 新增：区分本地和远程数据
  localSourceCategoriesCount?: number;
  localSourcePromptsCount?: number;
  remoteSourceCategoriesCount?: number;
  remoteSourcePromptsCount?: number;
}

const ImportConfirmModal: React.FC<ImportConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  localCategoriesCount,
  localPromptsCount,
  importCategoriesCount,
  importPromptsCount,
  isRemoteSync = false,
  localSourceCategoriesCount = 0,
  localSourcePromptsCount = 0,
  remoteSourceCategoriesCount = 0,
  remoteSourcePromptsCount = 0,
}) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div className="text-center py-4">
          {/* 警告图标 */}
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg mx-auto mb-5">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          {/* 标题和说明 */}
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {isRemoteSync ? '确认远程同步' : '确认导入数据'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
            {isRemoteSync
              ? '即将从远程服务器同步数据到本地'
              : '即将从本地文件导入数据'}
          </p>

          {/* 数据统计对比 */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
            <div className="space-y-3">
              {isRemoteSync ? (
                <>
                  {/* 远程同步：详细展示本地和远程数据 */}
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      📦 本地数据（将保留）
                    </div>
                    <div className="pl-3 space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">• 本地创建的分类：</span>
                        <span className="font-medium text-green-600 dark:text-green-400">{localSourceCategoriesCount}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">• 本地创建的提示词：</span>
                        <span className="font-medium text-green-600 dark:text-green-400">{localSourcePromptsCount}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                  
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      🗑️ 远程数据（将删除）
                    </div>
                    <div className="pl-3 space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">• 旧的远程分类：</span>
                        <span className="font-medium text-red-600 dark:text-red-400">{remoteSourceCategoriesCount}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">• 旧的远程提示词：</span>
                        <span className="font-medium text-red-600 dark:text-red-400">{remoteSourcePromptsCount}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                  
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      ⬇️ 待同步数据（将添加/更新）
                    </div>
                    <div className="pl-3 space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">• 远程分类：</span>
                        <span className="font-medium text-blue-600 dark:text-blue-400">{importCategoriesCount}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">• 远程提示词：</span>
                        <span className="font-medium text-blue-600 dark:text-blue-400">{importPromptsCount}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                  
                  <div className="space-y-2 bg-blue-50 dark:bg-blue-900/20 rounded-md p-2">
                    <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">
                      ✅ 同步后预期数据
                    </div>
                    <div className="pl-2 space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-blue-600 dark:text-blue-400">总分类数量：</span>
                        <span className="font-bold text-blue-700 dark:text-blue-300">
                          {localSourceCategoriesCount + importCategoriesCount}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-blue-600 dark:text-blue-400">总提示词数量：</span>
                        <span className="font-bold text-blue-700 dark:text-blue-300">
                          {localSourcePromptsCount + importPromptsCount}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* 本地导入：简单展示 */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">当前分类数量：</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{localCategoriesCount}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">当前提示词数量：</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{localPromptsCount}</span>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">导入分类数量：</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">{importCategoriesCount}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">导入提示词数量：</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">{importPromptsCount}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 覆盖警告 */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg
                  className="w-5 h-5 text-amber-600 dark:text-amber-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="flex-1 text-left">
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-1">
                  ⚠️ 重要提示
                </h4>
                <p className="text-sm text-amber-800 dark:text-amber-400">
                  {isRemoteSync ? '同步' : '导入'}操作将会覆盖相同ID的本地数据。
                  新数据将被添加，现有数据将被更新。此操作不可撤销，请确认后继续。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 按钮 */}
        <div className="flex justify-end space-x-3 pt-2">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
          >
            确认{isRemoteSync ? '同步' : '导入'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ImportConfirmModal;

